import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { PieChart, ArrowRight, Info, ShieldCheck, AlertTriangle } from "lucide-react";
import { useSEO } from "../lib/useSEO";

const SITE_URL = "https://instabridgefunding.com";
const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

// All waterfall math is server-computed via /api/public/calc/settlement-distribution —
// no client-side duplication.
async function fetchDistribution(payload) {
  const res = await fetch(`${API}/api/public/calc/settlement-distribution`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`distribution API ${res.status}`);
  return res.json();
}

function Field({ label, value, setValue, min = 0, max, step = 100, testid, suffix }) {
  return (
    <label className="block mb-4" data-testid={`field-${testid}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium text-[#0A2540]">{label}</span>
        <span className="text-sm text-[#0066FF] font-semibold">
          {suffix ? `${value}${suffix}` : fmt(value)}
        </span>
      </div>
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => setValue(Number(e.target.value) || 0)}
        className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:border-[#0066FF]"
        data-testid={`input-${testid}`}
      />
    </label>
  );
}

export default function SettlementDistributionCalculator() {
  useSEO(
    "Settlement Distribution Calculator | Instabridge Funding",
    "See exactly what you take home from a personal-injury settlement. Runs the full waterfall: attorney fees, case costs, MDL common-benefit, Medicare, ERISA, hospital liens, funding advance, and net-to-plaintiff.",
    { canonical: `${SITE_URL}/tools/settlement-distribution-calculator` },
  );

  const [gross, setGross] = useState(150000);
  const [feePct, setFeePct] = useState(33.33);
  const [costs, setCosts] = useState(4500);
  const [mdlPct, setMdlPct] = useState(0);
  const [medicare, setMedicare] = useState(0);
  const [erisa, setErisa] = useState(0);
  const [hospital, setHospital] = useState(0);
  const [otherLiens, setOtherLiens] = useState(0);
  const [funding, setFunding] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      fetchDistribution({
        gross,
        attorney_fee_pct: feePct,
        case_costs: costs,
        mdl_common_benefit_pct: mdlPct,
        medicare,
        erisa,
        hospital,
        other_liens: otherLiens,
        funding_payoff: funding,
      })
        .then((r) => { if (!cancelled) { setResult(r); setError(null); } })
        .catch((e) => { if (!cancelled) setError(e.message); });
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [gross, feePct, costs, mdlPct, medicare, erisa, hospital, otherLiens, funding]);

  const rows = {
    fee: result?.attorney_fee ?? 0,
    mdlAmount: result?.mdl_amount ?? 0,
    totalLiens: result?.total_liens ?? 0,
    net: result?.net_to_plaintiff ?? 0,
  };
  const takeHomePct = result?.take_home_pct ?? 0;
  const feeWarning = result?.attorney_fee_pct_warning;

  const waterfall = [
    { label: "Gross settlement", amount: gross, sign: "+", strong: true },
    { label: `Attorney fee (${feePct.toFixed(2)}%)`, amount: rows.fee, sign: "−" },
    { label: "Case costs (experts, filings, mediation)", amount: costs, sign: "−" },
    ...(mdlPct > 0
      ? [{ label: `MDL common-benefit (${mdlPct.toFixed(1)}%)`, amount: rows.mdlAmount, sign: "−" }]
      : []),
    ...(medicare > 0 ? [{ label: "Medicare MSP (post-negotiation)", amount: medicare, sign: "−" }] : []),
    ...(erisa > 0 ? [{ label: "ERISA plan lien (post-negotiation)", amount: erisa, sign: "−" }] : []),
    ...(hospital > 0 ? [{ label: "Hospital / provider liens", amount: hospital, sign: "−" }] : []),
    ...(otherLiens > 0
      ? [{ label: "Other liens (Medicaid, comp, prior counsel)", amount: otherLiens, sign: "−" }]
      : []),
    ...(funding > 0 ? [{ label: "Pre-settlement funding payoff", amount: funding, sign: "−" }] : []),
    { label: "Net to plaintiff", amount: rows.net, sign: "=", strong: true, highlight: true },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="settlement-distribution-calculator-page">
      <Navbar />
      <header className="border-b border-[#E5E7EB] bg-gradient-to-b from-[#F9FAFB] to-white">
        <div className="mx-auto max-w-5xl px-6 lg:px-10 pt-10 lg:pt-14 pb-10">
          <div className="uppercase-label mb-4">Interactive Tool</div>
          <h1
            className="font-display text-3xl sm:text-4xl lg:text-[3.25rem] text-[#0A2540] font-semibold tracking-tight leading-[1.1]"
            data-testid="tool-title"
          >
            Settlement Distribution Calculator
          </h1>
          <p className="mt-4 text-lg text-[#374151] leading-relaxed max-w-3xl">
            See exactly what a settlement will pay you at closing — after attorney fees, costs, liens, MDL common-benefit, and any pre-settlement funding. The full waterfall, transparent math.
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 lg:px-10 py-12 lg:py-16">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          {/* Inputs */}
          <section className="rounded-2xl border border-[#E5E7EB] p-8 bg-white shadow-sm" data-testid="distribution-inputs">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0066FF] to-[#001A3D] text-white flex items-center justify-center">
                <PieChart className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#6B7280] font-medium">Inputs</div>
                <div className="font-display text-lg text-[#0A2540] font-semibold">Settlement Terms</div>
              </div>
            </div>

            <Field label="Gross settlement" value={gross} setValue={setGross} step={1000} testid="gross" />
            <Field label="Attorney fee %" value={feePct} setValue={setFeePct} step={0.5} max={45} testid="fee-pct" suffix="%" />
            <Field label="Case costs" value={costs} setValue={setCosts} step={100} testid="costs" />

            <div className="mt-6 mb-3 uppercase-label text-[#6B7280]">MDL cases only</div>
            <Field label="Common-benefit assessment %" value={mdlPct} setValue={setMdlPct} step={0.5} max={12} testid="mdl-pct" suffix="%" />

            <div className="mt-6 mb-3 uppercase-label text-[#6B7280]">Liens (post-negotiation)</div>
            <Field label="Medicare MSP" value={medicare} setValue={setMedicare} testid="medicare" />
            <Field label="ERISA plan" value={erisa} setValue={setErisa} testid="erisa" />
            <Field label="Hospital / provider liens" value={hospital} setValue={setHospital} testid="hospital" />
            <Field label="Other liens (Medicaid, workers&apos; comp, prior counsel)" value={otherLiens} setValue={setOtherLiens} testid="other-liens" />

            <div className="mt-6 mb-3 uppercase-label text-[#6B7280]">Funding</div>
            <Field label="Pre-settlement funding payoff" value={funding} setValue={setFunding} testid="funding-payoff" />
          </section>

          {/* Waterfall Results */}
          <section className="space-y-5" data-testid="distribution-results">
            <div className="rounded-2xl bg-gradient-to-br from-[#0A2540] to-[#001A3D] text-white p-8">
              <div className="uppercase-label text-[#FAD28C] mb-3">Net to plaintiff</div>
              <div className="font-display text-5xl font-semibold" data-testid="result-net">
                {fmt(rows.net)}
              </div>
              <div className="mt-2 text-white/70 text-sm" data-testid="result-take-home-pct">
                {takeHomePct.toFixed(1)}% of gross settlement
              </div>
              <div className="mt-6 h-3 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#0066FF] to-[#00D1FF] transition-all"
                  style={{ width: `${Math.max(0, Math.min(100, takeHomePct))}%` }}
                  data-testid="result-net-bar"
                />
              </div>
            </div>

            <div className="rounded-2xl border border-[#E5E7EB] overflow-hidden" data-testid="waterfall-table">
              <table className="w-full">
                <tbody>
                  {waterfall.map((r, i) => (
                    <tr
                      key={i}
                      className={`border-b border-[#F3F4F6] last:border-b-0 ${
                        r.highlight ? "bg-[#F0F6FF]" : r.strong ? "bg-[#F9FAFB]" : ""
                      }`}
                    >
                      <td className={`py-3 px-5 ${r.strong ? "font-semibold text-[#0A2540]" : "text-[#374151]"} text-sm`}>
                        <span className="inline-block w-6 text-[#6B7280]">{r.sign}</span>
                        {r.label}
                      </td>
                      <td className={`py-3 px-5 text-right ${r.strong ? "font-semibold text-[#0A2540]" : "text-[#374151]"} text-sm`}>
                        {fmt(r.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] p-5 flex items-start gap-3">
              <Info className="w-5 h-5 text-[#0066FF] flex-shrink-0 mt-0.5" />
              <div className="text-sm text-[#374151]">
                Lien amounts should be the <strong>post-negotiation</strong> figures your attorney has confirmed —
                not initial demand amounts. Made-whole, common-fund, and Medicare&apos;s procurement-cost reduction
                routinely produce 20–50% reductions from initial demands.
              </div>
            </div>

            <div className="rounded-2xl border border-[#E5E7EB] bg-white p-5 flex items-start gap-3" data-testid="priority-note">
              <ShieldCheck className="w-5 h-5 text-[#0066FF] flex-shrink-0 mt-0.5" />
              <div className="text-sm text-[#374151]">
                <strong>Contract priority.</strong> Per your Instabridge Non-Recourse Purchase Agreement, the funding
                payoff is subordinate <em>only</em> to attorney fees, case costs, and medical liens. Everything else in
                the waterfall runs against the Seller&apos;s share.
              </div>
            </div>

            {feeWarning ? (
              <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 flex items-start gap-3" data-testid="fee-warning">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">{feeWarning}</div>
              </div>
            ) : null}

            {error ? (
              <div className="text-[12px] text-red-600" data-testid="calc-error">
                Calculator unavailable — {error}
              </div>
            ) : null}
          </section>
        </div>

        <div className="mt-14 rounded-2xl bg-gradient-to-br from-[#0A2540] to-[#001A3D] text-white p-10">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="uppercase-label text-[#FAD28C] mb-3">Need cash while you wait for settlement?</div>
              <h2 className="font-display text-3xl font-semibold">Non-recourse advances from $500 to $25,000.</h2>
              <p className="mt-3 text-white/80">
                Funds in 24–48 hours. Owe nothing if you lose. See what your case qualifies for.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <Link to="/apply">
                <Button
                  className="rounded-full h-12 px-7 bg-white text-[#0A2540] hover:bg-white/90 font-medium"
                  data-testid="tool-cta-apply"
                >
                  Apply now <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
              <Link to="/tools/funding-cost-calculator">
                <Button
                  variant="ghost"
                  className="rounded-full h-12 px-7 text-white hover:bg-white/10"
                  data-testid="tool-cta-cross"
                >
                  Funding Cost Calculator
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
